import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SlotGroup } from '../types';

const DATE_PAGE_SIZE = 7;

interface DateCarouselProps {
  groups: SlotGroup[];
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}

/**
 * Date strip with prev/next navigation — ported from widget's vx-date-strip.
 * Shows 7 dates at a time with slot counts.
 */
export function DateCarousel({
  groups,
  selectedDate,
  onSelectDate,
}: DateCarouselProps): React.ReactElement {
  const [dateOffset, setDateOffset] = useState(0);

  const dates = useMemo(
    () => groups.map((g) => ({ dateKey: g.date, dateLabel: g.dayLabel, count: g.slots.length })),
    [groups]
  );

  const maxDateOffset = Math.max(0, dates.length - DATE_PAGE_SIZE);
  const visibleDates = useMemo(
    () => dates.slice(dateOffset, dateOffset + DATE_PAGE_SIZE),
    [dateOffset, dates]
  );

  // Keep selected date in view
  useEffect(() => {
    if (!selectedDate || dates.length === 0) return;
    const idx = dates.findIndex((d) => d.dateKey === selectedDate);
    if (idx < 0) return;
    if (idx < dateOffset) setDateOffset(idx);
    else if (idx >= dateOffset + DATE_PAGE_SIZE)
      setDateOffset(Math.min(idx, maxDateOffset));
  }, [selectedDate, dates, dateOffset, maxDateOffset]);

  function navigateDate(direction: 'prev' | 'next'): void {
    if (!selectedDate || dates.length === 0) return;
    const currentIdx = dates.findIndex((d) => d.dateKey === selectedDate);
    if (currentIdx < 0) return;
    const nextIdx = direction === 'next' ? currentIdx + 1 : currentIdx - 1;
    if (nextIdx < 0 || nextIdx >= dates.length) return;
    onSelectDate(dates[nextIdx]!.dateKey);
    if (nextIdx < dateOffset) setDateOffset(nextIdx);
    else if (nextIdx >= dateOffset + DATE_PAGE_SIZE)
      setDateOffset(nextIdx - DATE_PAGE_SIZE + 1);
  }

  if (dates.length === 0) return <></>;

  return (
    <div className="sp-date-strip">
      <button
        type="button"
        className="sp-nav-btn"
        onClick={() => navigateDate('prev')}
        disabled={!selectedDate || dates[0]?.dateKey === selectedDate}
        aria-label="Previous date"
      >
        <ChevronLeft size={16} />
      </button>

      <div className="sp-date-cards">
        {visibleDates.map((date) => (
          <button
            key={date.dateKey}
            type="button"
            className={cn(
              'sp-date-card',
              selectedDate === date.dateKey && 'sp-date-card-active'
            )}
            onClick={() => onSelectDate(date.dateKey)}
          >
            <div className="sp-date-card-label">{date.dateLabel}</div>
            <small className="sp-date-card-count">
              {date.count} {date.count === 1 ? 'slot' : 'slots'}
            </small>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="sp-nav-btn"
        onClick={() => navigateDate('next')}
        disabled={!selectedDate || dates[dates.length - 1]?.dateKey === selectedDate}
        aria-label="Next date"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
